import {
  addDaysToYmd,
  inclusiveDaySpanYmd,
  todayIsoLocal,
} from './usDateFormat';

export const ATTENDANCE_MAX_RANGE_DAYS = 15;
export const DEFAULT_ATTENDANCE_DAYS = 30;
export const ATTENDANCE_PERIOD_CUSTOM = 'custom';
export const ATTENDANCE_DAYS_FILTER_CUSTOM = 'custom';

export const ATTENDANCE_DAYS_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 15, label: 'Last 15 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
];

/** Default start/end for custom range mode (last 15 days inclusive). */
export function defaultCustomAttendanceRange() {
  const endDate = todayIsoLocal();
  const startDate = addDaysToYmd(endDate, -(ATTENDANCE_MAX_RANGE_DAYS - 1)) || endDate;
  return { startDate, endDate };
}

/** No dates selected by default; user may optionally pick a range. */
export function defaultAttendanceDateRange() {
  return { startDate: '', endDate: '' };
}

export function isAttendanceDateFilterActive(startDate, endDate) {
  return Boolean(startDate && endDate);
}

/** Latest allowed end date for a start date (start + 14 days, capped at today). */
export function maxEndDateForStart(startDate) {
  if (!startDate) return todayIsoLocal();
  const rangeCap = addDaysToYmd(startDate, ATTENDANCE_MAX_RANGE_DAYS - 1);
  const today = todayIsoLocal();
  let maxEnd = rangeCap && rangeCap < today ? rangeCap : today;
  if (maxEnd < startDate) {
    maxEnd = rangeCap || startDate;
  }
  return maxEnd;
}

/** Clamp end date when start changes or range exceeds max. */
export function normalizeAttendanceRange(startDate, endDate) {
  if (!startDate) {
    return { startDate: '', endDate: '' };
  }
  let nextEnd = endDate || startDate;
  const maxEnd = maxEndDateForStart(startDate);
  if (!nextEnd || nextEnd < startDate) {
    nextEnd = startDate;
  }
  if (maxEnd && nextEnd > maxEnd) {
    nextEnd = maxEnd;
  }
  return { startDate, endDate: nextEnd };
}

export function getAttendanceRangeError(startDate, endDate) {
  if (!startDate && !endDate) {
    return null;
  }
  if (!startDate || !endDate) {
    return 'Select both start date and end date to filter by date range.';
  }
  if (endDate < startDate) {
    return 'End date must be on or after the start date.';
  }
  const span = inclusiveDaySpanYmd(startDate, endDate);
  if (span > ATTENDANCE_MAX_RANGE_DAYS) {
    return `Date range cannot exceed ${ATTENDANCE_MAX_RANGE_DAYS} days.`;
  }
  return null;
}

export function isValidAttendanceRange(startDate, endDate) {
  return getAttendanceRangeError(startDate, endDate) === null;
}

export function canFetchWithAttendanceDateFilter(startDate, endDate) {
  if (!startDate && !endDate) {
    return true;
  }
  return isAttendanceDateFilterActive(startDate, endDate) && isValidAttendanceRange(startDate, endDate);
}

export function buildAttendanceHistoryDateParams(
  startDate,
  endDate,
  days = DEFAULT_ATTENDANCE_DAYS
) {
  if (isAttendanceDateFilterActive(startDate, endDate)) {
    return { start_date: startDate, end_date: endDate };
  }
  return { days };
}

export function buildAttendanceAllByDateParams(
  startDate,
  endDate,
  workDate,
  period = 'day'
) {
  if (period === ATTENDANCE_PERIOD_CUSTOM && isAttendanceDateFilterActive(startDate, endDate)) {
    return { start_date: startDate, end_date: endDate };
  }
  return { work_date: workDate || todayIsoLocal(), period };
}

export function formatAttendanceHistorySummary(days, startDate, endDate, formatUsDateFromIso) {
  const rangeLabel = formatAttendanceRangeLabel(startDate, endDate, formatUsDateFromIso);
  if (rangeLabel) {
    return `Showing attendance for ${rangeLabel}`;
  }
  return `Showing last ${days} days`;
}

export function formatAttendanceRangeLabel(startDate, endDate, formatUsDateFromIso) {
  if (!startDate || !endDate) return '';
  const startLabel = formatUsDateFromIso(startDate) || startDate;
  const endLabel = formatUsDateFromIso(endDate) || endDate;
  return `${startLabel} – ${endLabel}`;
}

export function formatAttendanceFilterSummary(startDate, endDate, formatUsDateFromIso) {
  const rangeLabel = formatAttendanceRangeLabel(startDate, endDate, formatUsDateFromIso);
  if (rangeLabel && isValidAttendanceRange(startDate, endDate)) {
    return `Showing attendance for ${rangeLabel}`;
  }
  if (rangeLabel) {
    return `Custom range: ${rangeLabel}`;
  }
  return "Select a custom date range";
}
